import React from "react";

const sections = [
  {
    title: "1. Introduction",
    content: (
      <>
        <p>
          Welcome to <strong>Uptime Monitor</strong> (&quot;we,&quot; &quot;our,&quot;
          &quot;us&quot;). These Terms of Service (&quot;Terms&quot;) govern your access to and use
          of our website located at{" "}
          <a href="https://uptime-monitor.io" className="text-blue-600 underline">
            https://uptime-monitor.io
          </a>{" "}
          (&quot;Site&quot;) and any related services (&quot;Service&quot;). By accessing or using
          our Service, you agree to be bound by these Terms. If you do not agree with any part of
          these Terms, please do not use our Service.
        </p>
      </>
    ),
  },
  {
    title: "2. Acceptance of Terms",
    content: (
      <>
        <p>
          By creating an account, accessing, or using our Service, you affirm that you are at least
          18 years old and capable of entering into a legally binding agreement. If you are using
          the Service on behalf of an organization, you represent that you have the authority to
          bind that organization to these Terms.
        </p>
      </>
    ),
  },
  {
    title: "3. Changes to Terms",
    content: (
      <>
        <p>
          We may modify these Terms at any time. Any changes will be effective immediately upon
          posting the revised Terms on our Site. Your continued use of the Service after such
          changes constitutes your acceptance of the new Terms. We encourage you to review the Terms
          periodically for any updates.
        </p>
      </>
    ),
  },
  {
    title: "4. Account Registration and Responsibilities",
    subsections: [
      {
        subtitle: "4.1. Account Creation",
        content: (
          <>
            <p>
              To access certain features of the Service, you may need to create an account. You
              agree to provide accurate, current, and complete information during the registration
              process and to update such information to keep it accurate.
            </p>
          </>
        ),
      },
      {
        subtitle: "4.2. Account Security",
        content: (
          <>
            <p>
              You are responsible for maintaining the confidentiality of your account credentials,
              including your username and password. You agree to notify us immediately of any
              unauthorized use of your account or any other breach of security.
            </p>
          </>
        ),
      },
      {
        subtitle: "4.3. Account Termination",
        content: (
          <>
            <p>
              We reserve the right to suspend or terminate your account at our sole discretion,
              without notice, for conduct that we believe violates these Terms or is harmful to
              other users of the Service, us, or third parties, or for any other reason.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "5. Use of the Service",
    subsections: [
      {
        subtitle: "5.1. Permitted Use",
        content: (
          <>
            <p>
              Uptime Monitor provides tools to monitor the uptime and performance of websites and
              services. You may use the Service for lawful purposes only and in accordance with
              these Terms.
            </p>
          </>
        ),
      },
      {
        subtitle: "5.2. Prohibited Activities",
        content: (
          <>
            <p>You agree not to:</p>
            <ul className="list-disc list-inside ml-4">
              <li>Use the Service for any illegal or unauthorized purpose.</li>
              <li>Interfere with or disrupt the integrity or performance of the Service.</li>
              <li>Attempt to gain unauthorized access to any portion of the Service.</li>
              <li>
                Use automated means to access the Service without our prior written permission.
              </li>
              <li>
                Reproduce, duplicate, copy, sell, or otherwise exploit any portion of the Service
                without express written permission.
              </li>
            </ul>
          </>
        ),
      },
    ],
  },
  {
    title: "6. Subscription Plans and Payment",
    subsections: [
      {
        subtitle: "6.1. Subscription Plans",
        content: (
          <>
            <p>
              We offer various subscription plans and paid features with different functionalities
              and pricing. Details of available plans and features are provided on our Site and may
              be updated from time to time.
            </p>
          </>
        ),
      },
      {
        subtitle: "6.2. Payment Terms",
        content: (
          <>
            <ul className="list-disc list-inside ml-4">
              <li>
                <strong>Billing Cycle:</strong> Subscriptions are billed in advance on a monthly or
                annual basis, depending on the selected plan.
              </li>
              <li>
                <strong>Payment Methods:</strong> We use Stripe for payment processing. Stripe
                handles all payment transactions and provides subscription details to Uptime
                Monitor&apos;s servers.
              </li>
              <li>
                <strong>Price Changes:</strong> We reserve the right to modify the fees for our
                Service at any time. We will notify you of any changes in pricing at least 30 days
                prior to the new rates taking effect.
              </li>
            </ul>
          </>
        ),
      },
      {
        subtitle: "6.3. Refund Policy",
        content: (
          <>
            <p>All sales are final. We do not offer refunds.</p>
          </>
        ),
      },
    ],
  },
  {
    title: "7. Intellectual Property",
    subsections: [
      {
        subtitle: "7.1. Ownership",
        content: (
          <>
            <p>
              All content, trademarks, service marks, logos, and intellectual property displayed on
              the Site are the property of Uptime Monitor or their respective owners. You are
              granted a limited, non-exclusive, non-transferable license to access and use the
              Service for your personal or internal business purposes.
            </p>
          </>
        ),
      },
      {
        subtitle: "7.2. Restrictions",
        content: (
          <>
            <p>You may not:</p>
            <ul className="list-disc list-inside ml-4">
              <li>Modify, distribute, or create derivative works based on the Service.</li>
              <li>Reverse engineer, decompile, or disassemble any part of the Service.</li>
              <li>Use our trademarks or logos without prior written consent.</li>
            </ul>
          </>
        ),
      },
    ],
  },
  {
    title: "8. User Content",
    subsections: [
      {
        subtitle: "8.1. Responsibility",
        content: (
          <>
            <p>
              You are solely responsible for any data, information, or content you upload, submit,
              or otherwise make available through the Service (&quot;User Content&quot;). You retain
              ownership of your User Content.
            </p>
          </>
        ),
      },
      {
        subtitle: "8.2. License Grant",
        content: (
          <>
            <p>
              By uploading User Content, you grant us a worldwide, non-exclusive, royalty-free
              license to use, reproduce, modify, and display your User Content solely for the
              purpose of providing the Service.
            </p>
          </>
        ),
      },
      {
        subtitle: "8.3. Prohibited Content",
        content: (
          <>
            <p>You agree not to upload any User Content that:</p>
            <ul className="list-disc list-inside ml-4">
              <li>Infringes on any third-party rights.</li>
              <li>Is unlawful, harmful, or offensive.</li>
              <li>Contains viruses or other malicious components.</li>
            </ul>
          </>
        ),
      },
    ],
  },
  {
    title: "9. Privacy Policy",
    content: (
      <>
        <p>
          Your use of the Service is also governed by our{" "}
          <a href="https://uptime-monitor.io/privacy_policy" className="text-blue-600 underline">
            Privacy Policy
          </a>
          , which describes how we collect, use, and protect your personal information. We aim to
          support GDPR compliance, including the removal of all customer data from our main database
          upon account deletion. However, some data may remain in Stripe&apos;s dashboard and our
          ClickHouse database, where it is anonymized using IDs instead of personal information.
        </p>
      </>
    ),
  },
  {
    title: "10. Termination",
    content: (
      <>
        <p>
          We may terminate or suspend your access to the Service immediately, without prior notice
          or liability, for any reason, including without limitation if you breach the Terms. Upon
          termination, your right to use the Service will cease immediately.
        </p>
      </>
    ),
  },
  {
    title: "11. Disclaimer of Warranties",
    content: (
      <>
        <p>
          The Service is provided &quot;as is&quot; and &quot;as available&quot; without any
          warranties of any kind, whether express or implied. We disclaim all warranties, including
          but not limited to the implied warranties of merchantability, fitness for a particular
          purpose, and non-infringement.
        </p>
      </>
    ),
  },
  {
    title: "12. Limitation of Liability",
    content: (
      <>
        <p>
          To the fullest extent permitted by law, Uptime Monitor shall not be liable for any
          indirect, incidental, special, consequential, or punitive damages, including without
          limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting
          from:
        </p>
        <ul className="list-disc list-inside ml-4">
          <li>Your use or inability to use the Service.</li>
          <li>
            Any unauthorized access to or use of our servers and/or any personal information stored
            therein.
          </li>
          <li>
            Any bugs, viruses, trojan horses, or the like that may be transmitted to or through our
            Service by any third party.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "13. Indemnification",
    content: (
      <>
        <p>
          You agree to defend, indemnify, and hold harmless Uptime Monitor, CodeMatters LLC, and
          their affiliates, licensors, and service providers from and against any claims,
          liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including
          reasonable attorneys&apos; fees) arising out of or relating to your violation of these
          Terms or your use of the Service.
        </p>
      </>
    ),
  },
  {
    title: "14. Governing Law",
    content: (
      <>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of the State of
          New York, County of Kings (Brooklyn), USA, without regard to its conflict of law
          provisions. You agree to submit to the personal and exclusive jurisdiction of the courts
          located within Kings County, New York, for the resolution of any disputes.
        </p>
      </>
    ),
  },
  {
    title: "15. Dispute Resolution",
    subsections: [
      {
        subtitle: "15.1. Arbitration",
        content: (
          <>
            <p>
              Any disputes arising out of or related to these Terms or the Service shall be resolved
              through binding arbitration in accordance with the rules of the American Arbitration
              Association then in effect.
            </p>
          </>
        ),
      },
      {
        subtitle: "15.2. Class Action Waiver",
        content: (
          <>
            <p>
              You agree that any arbitration under these Terms will be conducted on an individual
              basis and not as a class, collective, or representative action.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "16. Severability",
    content: (
      <>
        <p>
          If any provision of these Terms is found to be invalid or unenforceable by a court, the
          remaining provisions of these Terms will remain in full effect.
        </p>
      </>
    ),
  },
  {
    title: "17. Entire Agreement",
    content: (
      <>
        <p>
          These Terms constitute the entire agreement between you and Uptime Monitor regarding your
          use of the Service and supersede all prior and contemporaneous agreements, proposals, or
          communications, whether oral or written.
        </p>
      </>
    ),
  },
  {
    title: "18. Contact Information",
    content: (
      <>
        <p>If you have any questions about these Terms, please contact us at:</p>
        <p>
          <strong>Email:</strong>{" "}
          <a href="mailto:support@uptime-monitor.io" className="text-blue-600 underline">
            support@uptime-monitor.io
          </a>
        </p>
      </>
    ),
  },
];

const Tos: React.FC = () => {
  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Terms of Service</h1>
      <p className="mb-8">Last Updated: November 7, 2024</p>
      {sections.map((section, index) => (
        <div key={`section-${index}`} className="mb-8">
          <h2 id={`section-${index + 1}`} className="text-2xl font-semibold mb-4">
            {section.title}
          </h2>
          {section.content}
          {section.subsections &&
            section.subsections.map((sub, subIndex) => (
              <div key={`section-${index}-sub-${subIndex}`} className="mt-6">
                <h3 className="text-xl font-medium mb-2">{sub.subtitle}</h3>
                {sub.content}
              </div>
            ))}
        </div>
      ))}
    </div>
  );
};

export default Tos;
